import { useQueryClient } from '@tanstack/react-query';
import { dequal } from 'dequal';
import * as React from 'react';
import type { TempoUserResponse } from '@/lib/api/types/userManagement';
import { userManagementQueryKeys } from '@/lib/query-keys/userManagement';
import { buildIdKeyMatcher } from '../hooks/buildIdKeyMatcher';
import {
  useVisibleUsersQuery,
  type VisibleUsersData,
  visibleUsersQueryKey,
} from '../hooks/useVisibleUsersQuery';

const EMPTY_USERS: ReadonlyMap<string, TempoUserResponse> = new Map();
const EMPTY_MISSING_USER_IDS: ReadonlySet<string> = new Set();
const DEBOUNCE_MS = 50;
const matchUserKey = buildIdKeyMatcher<string>(userManagementQueryKeys.users.byId);

type RegistrationEntry = { count: number };

type VisibleUsersContextType = {
  usersById: ReadonlyMap<string, TempoUserResponse>;
  missingUserIds: ReadonlySet<string>;
  registerUserId: (userId: string) => void;
  unregisterUserId: (userId: string) => void;
};

const VisibleUsersContext = React.createContext<VisibleUsersContextType | undefined>(undefined);

export const useVisibleUsers = (): VisibleUsersContextType => {
  const context = React.useContext(VisibleUsersContext);
  if (!context) {
    return {
      usersById: EMPTY_USERS,
      missingUserIds: EMPTY_MISSING_USER_IDS,
      registerUserId: () => {},
      unregisterUserId: () => {},
    };
  }
  return context;
};

export const useVisibleUser = (id: string | undefined): TempoUserResponse | undefined => {
  const { usersById } = useVisibleUsers();
  if (id === undefined) return undefined;
  return usersById.get(id);
};

export const useIsUserMissing = (id: string | undefined): boolean => {
  const { missingUserIds } = useVisibleUsers();
  return id !== undefined && missingUserIds.has(id);
};

export const useRegisterVisibleUserId = (id: string | undefined): void => {
  const { registerUserId, unregisterUserId } = useVisibleUsers();
  React.useEffect(() => {
    if (!id) return;
    registerUserId(id);
    return () => unregisterUserId(id);
  }, [id, registerUserId, unregisterUserId]);
};

export const useRegisterVisibleUserIds = (ids: readonly string[] | undefined): void => {
  const { registerUserId, unregisterUserId } = useVisibleUsers();
  const idsKey = ids?.join('|') ?? '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: idsKey is a stable string derived from ids
  React.useEffect(() => {
    if (!ids?.length) return;
    for (const id of ids) registerUserId(id);
    return () => {
      for (const id of ids) unregisterUserId(id);
    };
  }, [idsKey, registerUserId, unregisterUserId]);
};

export const VisibleUsersProvider = ({
  instanceKey,
  enabled = true,
  children,
}: {
  instanceKey: string;
  enabled?: boolean;
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();
  const countsRef = React.useRef(new Map<string, RegistrationEntry>());
  const [debouncedIds, setDebouncedIds] = React.useState<readonly string[]>([]);
  const debounceTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = React.useRef(true);

  const flush = React.useCallback(() => {
    if (!isMountedRef.current) return;
    setDebouncedIds(Array.from(countsRef.current.keys()));
  }, []);

  const schedule = React.useCallback(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(flush, DEBOUNCE_MS);
  }, [flush]);

  const registerUserId = React.useCallback(
    (userId: string) => {
      const map = countsRef.current;
      const existing = map.get(userId);
      if (existing) {
        existing.count += 1;
        return;
      }
      map.set(userId, { count: 1 });
      schedule();
    },
    [schedule],
  );

  const unregisterUserId = React.useCallback(
    (userId: string) => {
      const map = countsRef.current;
      const existing = map.get(userId);
      if (!existing) return;
      existing.count -= 1;
      if (existing.count <= 0) {
        map.delete(userId);
        schedule();
      }
    },
    [schedule],
  );

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  const { data } = useVisibleUsersQuery({ instanceKey, ids: debouncedIds, enabled });

  // Reflect cross-app writes/refetches of userManagementQueryKeys.users.byId(id)
  // into the accumulated users map.
  React.useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      const id = matchUserKey(event.query.queryKey);
      if (id === null) return;
      const value = event.query.state.data as TempoUserResponse | undefined;
      if (value === undefined) return;
      const key = visibleUsersQueryKey(instanceKey);
      const current = queryClient.getQueryData<VisibleUsersData>(key);
      if (!current) return;
      if (dequal(current.valuesById.get(id), value)) return;
      const next = new Map(current.valuesById);
      next.set(id, value);
      queryClient.setQueryData<VisibleUsersData>(key, {
        valuesById: next,
        missingIds: current.missingIds,
      });
    });
  }, [queryClient, instanceKey]);

  const value = React.useMemo<VisibleUsersContextType>(
    () => ({
      usersById: data?.valuesById ?? EMPTY_USERS,
      missingUserIds: data?.missingIds ?? EMPTY_MISSING_USER_IDS,
      registerUserId,
      unregisterUserId,
    }),
    [data, registerUserId, unregisterUserId],
  );

  return <VisibleUsersContext.Provider value={value}>{children}</VisibleUsersContext.Provider>;
};
